import React from 'react';

// ** UI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import SearchOffIcon from '@mui/icons-material/SearchOff';

// ** Components
import Section from '@/components/common/Section';
import FilterCardsGrid from '@/components/common/FilterCardsGrid';
import { PartnerCard } from '@/components/common/PartnerCards';

// ** Utils
import { getStrapiImageUrl } from '@/utils/api/strapi';
import { useGetPartnerCategories } from '@/lib/utils/partnerDataUtils';

// ** Types
import type { StrapiComponentProps } from '@/types/strapi';
import type { ComponentBlocksPartnerBlock as BlocksPartnerBlockProps } from '@/types/graphql';

type ComponentBlocksPartnerBlockProps = StrapiComponentProps<BlocksPartnerBlockProps>;

const ComponentBlocksPartnerBlock = ({
	host,
	containerSx,
	partnerContent,
	partnerFilterTools,
	partnerSearchFilter,
	partnerSectionTitle
}: ComponentBlocksPartnerBlockProps) => {
	// 1. Fetch real categories from the CMS via hook
	const { categories, loading } = useGetPartnerCategories({
		allLabel: partnerSearchFilter?.searchFilterCategoryPlaceholder || 'Alle Kategorien'
	});

	// 2. State for Filter and Search
	const [searchTerm, setSearchTerm] = React.useState('');
	const [selectedCategory, setSelectedCategory] = React.useState('all');

	// 3. Combined Filter Logic (Memoized)
	const visiblePartners = React.useMemo(() => {
		if (!partnerContent) return [];

		return partnerContent.filter((item) => {
			const data = item?.partnerData;
			if (!data) return false;

			// Category Match
			const matchesCategory = selectedCategory === 'all' || data.partnerCategory === selectedCategory;

			// Search Match (Name or Email)
			const searchStr = searchTerm.toLowerCase();
			const matchesSearch =
				!searchTerm ||
				data.partnerName?.toLowerCase().includes(searchStr) ||
				data.partnerEmail?.toLowerCase().includes(searchStr);

			return matchesCategory && matchesSearch;
		});
	}, [selectedCategory, searchTerm, partnerContent]);

	return (
		<Section backgroundColor="#F6F4F2" containerSx={containerSx}>
			{partnerSectionTitle && (
				<Typography
					variant="title-lg"
					component="h2"
					sx={{
						mb: 10,
						color: 'common.black',
						fontSize: { xs: '2rem', md: '2.5rem' },
						lineHeight: { xs: '2.5rem', md: '3rem' },
						letterSpacing: { xs: '-0.64px', md: '-1.2px' }
					}}
				>
					{partnerSectionTitle}
				</Typography>
			)}

			<FilterCardsGrid
				listLabel="Partner overview"
				showFilter={!!partnerFilterTools}
				filterSelect={{
					id: 'partner-category-filter',
					value: selectedCategory,
					searchValue: searchTerm,
					placeholder: partnerSearchFilter?.searchFilterCategoryPlaceholder || 'Filter nach Kategorie',
					options: categories,
					onChange: setSelectedCategory,
					onSearchChange: setSearchTerm,
					disabled: loading,
					showCategories: partnerSearchFilter?.searchFilterShowCategory ?? false,
					showSearch: partnerSearchFilter?.searchFilterShowSearch ?? false
				}}
			>
				{visiblePartners.map((item, index) => {
					const data = item?.partnerData;
					if (!data) return null;

					const logoUrl = getStrapiImageUrl({
						host,
						imageData: data.logo,
						isMobile: false
					});

					return (
						<PartnerCard
							key={data.partnerName || `partner-${index}`}
							id={data.partnerName || `${index}`}
							host={host}
							headline={data.partnerName || ''}
							subline={data.partnerDescription || ''}
							address={data.partnerAddress || ''}
							email={data.partnerEmail || ''}
							phone={data.partnerTelephone || ''}
							website={data.partnerUrl || ''}
							logoSrc={logoUrl || undefined}
							logoAlt={data.partnerName || 'Partner logo'}
						/>
					);
				})}
			</FilterCardsGrid>

			{!loading && visiblePartners.length === 0 && (searchTerm || selectedCategory !== 'all') && (
				<Box
					sx={{
						py: 10,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						gap: 2,
						color: 'text.secondary'
					}}
				>
					<SearchOffIcon sx={{ fontSize: 56, opacity: 0.4 }} />
					<Typography variant="body-lg" sx={{ fontWeight: 500 }}>
						Keine Partner gefunden.
					</Typography>
					<Typography variant="body-md" sx={{ opacity: 0.7 }}>
						Bitte passen Sie Ihre Suche oder Filter an.
					</Typography>
				</Box>
			)}
		</Section>
	);
};

export default ComponentBlocksPartnerBlock;
